/**
 * Shared utilities for Datadog RUM tracking across authentication flows
 */

// Disable Datadog actions on localhost
function isLocalhost(): boolean {
	const host = window.location.hostname
	return host === 'localhost' || host === '127.0.0.1' || host === '::1'
}

/** Skip RUM helpers on My Account surfaces (path, query, hash). */
function isMyAccountUrl(): boolean {
	return window.location.href.toLowerCase().includes('/myaccount')
}

function shouldSendDatadogEvents(): boolean {
	return !isLocalhost() && !isMyAccountUrl()
}

const isNonProduction = process.env.NODE_ENV !== 'production'

// Type declaration for Datadog RUM
declare global {
	interface Window {
		DD_RUM?: {
			addAction: (name: string, context?: any) => void
			addError: (error: Error, context?: any) => void
			onReady: (callback: () => void) => void
		}
	}
}

/**
 * Check if Datadog RUM is fully initialized (not just the object exists)
 */
function isDatadogReady(): boolean {
	return !!(
		window.DD_RUM &&
		typeof window.DD_RUM.addAction === 'function' &&
		typeof window.DD_RUM.addError === 'function'
	)
}

/**
 * Execute a function when Datadog RUM is ready
 * @param callback - The function to execute when Datadog is ready
 * @param timeout - Maximum time to wait in milliseconds (default: 5000)
 */
function whenDatadogReady(callback: () => void, timeout: number = 5000): void {
	if (isDatadogReady()) {
		callback()
		return
	}

	// If DD_RUM exists but methods aren't ready, use onReady if available
	if (window.DD_RUM && typeof window.DD_RUM.onReady === 'function') {
		window.DD_RUM.onReady(() => {
			callback()
		})
		return
	}

	// Fallback: poll for readiness
	const startTime = Date.now()
	const pollInterval = setInterval(() => {
		if (isDatadogReady()) {
			clearInterval(pollInterval)
			callback()
		} else if (Date.now() - startTime > timeout) {
			clearInterval(pollInterval)
		}
	}, 400)
}

/**
 * Send action data to Datadog RUM
 * @param source - The source identifier (e.g., 'relay-sso', 'relay-token', 'gbpe-auth-only', 'myaccount-nav')
 * @param action - The action name
 * @param context - Additional context data
 */
export function sendActionToDatadog(source: string, action: string, context?: any) {
	if (!shouldSendDatadogEvents()) return
	try {
		whenDatadogReady(() => {
			const actionContext = {
				source: source,
				timestamp: new Date().toISOString(),
				...context,
			}
			if (isNonProduction) {
				console.log(`Sending action to Datadog: ${action}`, actionContext)
			}
			window.DD_RUM!.addAction(action, actionContext)
		})
	} catch (error) {
		console.error('Error sending action to Datadog:', error)
	}
}

/**
 * Send error data to Datadog RUM
 * @param source - The source identifier (e.g., 'relay-sso', 'relay-token', 'gbpe-auth-only', 'myaccount-nav')
 * @param error - The error object
 * @param context - Additional context data
 */
export function sendErrorToDatadog(source: string, error: Error, context?: any) {
	if (!shouldSendDatadogEvents()) return
	try {
		whenDatadogReady(() => {
			const errorContext = {
				source: source,
				timestamp: new Date().toISOString(),
				...context,
			}
			if (isNonProduction) {
				console.log(`Sending error to Datadog: ${error.message}`, errorContext)
			}
			window.DD_RUM!.addError(error, errorContext)
		})
	} catch (ddError) {
		console.error('Error sending error to Datadog:', ddError)
	}
}

/**
 * Send region detection data to Datadog RUM
 * @param region - The detected region
 * @param reason - Optional reason for fallback scenarios
 * @param error - Optional error message for error scenarios
 */
export function sendRegionToDatadog(region: string, reason?: string, error?: string) {
	sendActionToDatadog('gbpe-auth-only', 'region_detected', {
		region: region,
		...(reason && { reason }),
		...(error && { error }),
	})
}

/**
 * Send region error data to Datadog RUM
 * @param region - The region context
 * @param reason - The reason for the error
 * @param error - The error object
 */
export function sendRegionErrorToDatadog(region: string, reason: string, error: Error) {
	sendErrorToDatadog('gbpe-auth-only', error, {
		region: region,
		reason: reason,
	})
}

/**
 * Send SSO action data to Datadog RUM
 * @param action - The action name
 * @param context - Additional context data
 */
export function sendSSOActionToDatadog(action: string, context?: any) {
	sendActionToDatadog('relay-sso', action, context)
}

/**
 * Send SSO error data to Datadog RUM
 * @param error - The error object
 * @param context - Additional context data
 */
export function sendSSOErrorToDatadog(error: Error, context?: any) {
	sendErrorToDatadog('relay-sso', error, context)
}

/**
 * Send relay token action data to Datadog RUM
 * @param action - The action name
 * @param context - Additional context data
 */
export function sendRelayTokenActionToDatadog(action: string, context?: any) {
	sendActionToDatadog('relay-token', action, context)
}

/**
 * Send relay token error data to Datadog RUM
 * @param error - The error object
 * @param context - Additional context data
 */
export function sendRelayTokenErrorToDatadog(error: Error, context?: any) {
	sendErrorToDatadog('relay-token', error, context)
}
